import { html, LitElement } from 'lit-element';
import isEqual from 'lodash-es/isEqual';
import groupBy from 'lodash-es/groupBy';
import cloneDeep from 'lodash-es/cloneDeep';
import { CTModal } from './modals/CTModal.js';
import { CTGroup } from './CTGroup.js';
import { Classifier } from './classifier/classifierTeacherLive.js';
import { PieMathRenderer } from './common/PieMathRenderer.js';

import { styles } from './styles.js';
import { defineElement, importMathRendering } from './classifier/utils.js';

/**
 * @element ct-teacher-live-item
 * @prop {Object} item - TODO
 * @prop {Array} sessions - TODO
 * @prop {Array} students - TODO
 * @prop {String} mathRenderingModuleUrl - TODO
 */
export class CTTeacherLiveItem extends LitElement {
  static get properties() {
    return {
      item: { type: Object },
      sessions: { type: Array },
      students: { type: Array },
      mathRenderingModuleUrl: { type: String },
    };
  }

  constructor() {
    super();

    this.ClassifierService = new Classifier();

    this.itemWithSessionsData = {};
    this.modalData = null;

    if (!customElements.get('ct-group')) {
      customElements.define('ct-group', CTGroup);
    }

    if (!customElements.get('ct-modal')) {
      customElements.define('ct-modal', CTModal);
    }

    this.setNewData = this.setNewData.bind(this);
  }

  createRenderRoot() {
    return this;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    defineElement('math-rendering-element', PieMathRenderer);

    if (!this.mathRenderingModuleUrlImported) {
      importMathRendering(this.mathRenderingModuleUrl);

      this.mathRenderingModuleUrlImported = true;
    }

    _changedProperties.forEach((oldValue, propName) => {
      const data = {};
      let shouldUpdate = false;

      if (this.item) {
        data.emitDataItemId = this.item.id;
      }

      if (propName === 'item') {
        data.items = [this.item];
        shouldUpdate = true;
      }

      if (propName === 'students') {
        data.students = this.students;
        shouldUpdate = true;
      }

      if (propName === 'sessions') {
        data.sessions = this.sessions;
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        this.ClassifierService.appendData(data);
      }
    });
  }

  setNewData(itemWithSessionsData) {
    this.itemWithSessionsData = cloneDeep(itemWithSessionsData);
    this.requestUpdate('itemWithSessionsData').then();
  }

  newData({ detail } = {}) {
    const { itemWithSessionsData } = detail || {};

    if (
      itemWithSessionsData &&
      !isEqual(itemWithSessionsData, this.itemWithSessionsData)
    ) {
      if (this.itemWithSessionsData.id !== itemWithSessionsData.id) {
        // if item changed, make sure to reset itemWithSessionsData
        // if we don't do this, then some of the old rendered data will stay on screen
        this.itemWithSessionsData = {};
        this.requestUpdate('itemWithSessionsData').then(() =>
          this.setNewData(itemWithSessionsData)
        );
      } else {
        this.setNewData(itemWithSessionsData);
      }
    }
  }

  openModal({ detail } = {}) {
    this.modalData = detail;

    this.requestUpdate('modalData').then();
  }

  closeModal() {
    this.modalData = null;

    this.requestUpdate('modalData').then();
  }

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener(
      'classroom-tool-teacher-live-new-data',
      this.newData.bind(this)
    );

    window.addEventListener(
      'classroom-tool-live-open-modal',
      this.openModal.bind(this)
    );

    window.addEventListener(
      'classroom-tool-live-close-modal',
      this.closeModal.bind(this)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'classroom-tool-teacher-live-new-data',
      this.newData.bind(this)
    );

    window.removeEventListener(
      'classroom-tool-live-open-modal',
      this.openModal.bind(this)
    );

    window.removeEventListener(
      'classroom-tool-live-close-modal',
      this.closeModal.bind(this)
    );
  }

  render() {
    const {
      headers,
      sessions,
      config,
      id,
      classroomToolLayout,
    } = this.itemWithSessionsData;

    const studentsAnsweredNr = Object.keys(sessions || {}).length;
    const groupedHeaders = groupBy(headers, 'correctnessGroup');
    const groupedSessions = groupBy(sessions, 'correctnessGroup');
    const sortedHeaders = Object.keys(groupedHeaders).sort();

    // noinspection CssUnresolvedCustomProperty
    return html`
      <style>
        ${styles}
      </style>

      <div class="ct-item">
        ${sortedHeaders.map(
          correctness => html`<ct-group
            .studentsAnsweredNr=${studentsAnsweredNr}
            .item=${{ id, config, classroomToolLayout }}
            .group=${{
              headers: groupedHeaders[correctness],
              sessions: groupedSessions[correctness],
            }}
            .correctness=${correctness}
          ></ct-group>`
        )}
        ${this.modalData
          ? html`<div class="modal">
              <ct-modal .modalData=${this.modalData}></ct-modal>
            </div>`
          : null}
      </div>
    `;
  }
}

export default CTTeacherLiveItem;
